// Microphone recording: MediaRecorder with a live input level (AnalyserNode).
//  - getUserMedia with echo cancellation, noise suppression, and auto gain.
//  - The first supported MIME type from MIME_CANDIDATES, else the browser default; 64 kbps.
//  - Chunks every TIMESLICE_MS so a crash mid-recording still leaves data (onChunk persists them).
//  - Duration comes from a timer: MediaRecorder WebM files carry no duration metadata.
//  - All tracks stop after recording, so the phone's "microphone in use" light goes off.
//  - iOS Safari: the AudioContext is created and resumed inside the user's tap, before any await.
import { logger } from '../lib/log.js';

const log = logger('recorder');

export const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/webm'];
export const TIMESLICE_MS = 1000;
const AUDIO_BITS = 64000;

/** What this browser can do. */
export function recordingSupport() {
  let hasGUM = false, hasMR = false;
  try { hasGUM = !!(navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function'); } catch { /* ignore */ }
  try { hasMR = typeof window.MediaRecorder === 'function'; } catch { /* ignore */ }
  return { ok: hasGUM && hasMR, hasGUM, hasMR, secure: window.isSecureContext !== false };
}

export function pickMimeType() {
  const MR = window.MediaRecorder;
  if (!MR || typeof MR.isTypeSupported !== 'function') return '';
  for (const t of MIME_CANDIDATES) {
    try { if (MR.isTypeSupported(t)) return t; } catch { /* ignore */ }
  }
  return '';
}

/** Sort a getUserMedia error into: denied | no-mic | busy | other. */
export function classifyMicError(err) {
  const n = (err && err.name) || '';
  if (n === 'NotAllowedError' || n === 'PermissionDeniedError' || n === 'SecurityError') return 'denied';
  if (n === 'NotFoundError' || n === 'DevicesNotFoundError' || n === 'OverconstrainedError' || n === 'ConstraintNotSatisfiedError') return 'no-mic';
  if (n === 'NotReadableError' || n === 'TrackStartError' || n === 'AbortError') return 'busy';
  return 'other';
}

/**
 * One recording at a time.
 * opts: { maxSeconds, onTick(sec), onLevel(level 0..1 RMS), onChunk(blob, seq, elapsedSec),
 *         onStop({ blob, mimeType, durationSec, reason, chunkCount }), meta (extra log fields) }
 */
export class Recorder {
  constructor(opts = {}) {
    this.o = opts;
    this.state = 'idle'; // idle | starting | recording | stopping
  }

  elapsed() { return this.startedAt ? (performance.now() - this.startedAt) / 1000 : 0; }

  /**
   * Call directly from the click handler. Resolves true once recording, false if stopped while
   * waiting for permission. Rejects with the getUserMedia error when the mic is unavailable.
   */
  async start() {
    if (this.state !== 'idle') return false;
    this.state = 'starting';
    this.stopReason = null;
    this.cancelStart = false;
    // iOS Safari only lets audio start inside a user gesture: create and resume now, before awaiting.
    const AC = window.AudioContext || window.webkitAudioContext;
    this.ctx = null;
    if (AC) {
      try {
        this.ctx = new AC();
        if (this.ctx.state === 'suspended' && this.ctx.resume) this.ctx.resume().catch(() => {});
      } catch (e) {
        this.ctx = null;
        log.warn('audio_context_failed', { errName: e && e.name, message: e && e.message });
      }
    }

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    } catch (err) {
      this.cleanup();
      this.state = 'idle';
      throw err;
    }
    this.stream = stream;
    if (this.cancelStart) {
      // stop() was called while we waited for permission (for example, the person left the page).
      this.cancelStart = false;
      this.cleanup();
      this.state = 'idle';
      return false;
    }

    const wanted = pickMimeType();
    let rec;
    try {
      rec = new MediaRecorder(stream, wanted ? { mimeType: wanted, audioBitsPerSecond: AUDIO_BITS } : { audioBitsPerSecond: AUDIO_BITS });
    } catch (e1) {
      log.warn('recorder_options_rejected', { wanted, errName: e1 && e1.name });
      try { rec = new MediaRecorder(stream); } catch (e2) { this.cleanup(); this.state = 'idle'; throw e2; }
    }
    this.rec = rec;
    this.chunks = [];
    this.seq = 0;
    this.mimeType = rec.mimeType || wanted || '';

    rec.ondataavailable = (e) => {
      if (!e.data || !e.data.size) return;
      this.chunks.push(e.data);
      if (!this.mimeType && e.data.type) this.mimeType = e.data.type;
      const seq = this.seq++;
      if (this.o.onChunk) { try { this.o.onChunk(e.data, seq, this.elapsed()); } catch { /* ignore */ } }
    };
    // The browser can end a recording on its own (microphone unplugged, a phone call on iOS).
    rec.onstop = () => { if (this.state === 'recording') this.stop('ended'); else this.finish(); };
    rec.onerror = (e) => {
      const err = e && (e.error || e);
      log.error('recorder_error', { errName: err && err.name, message: err && err.message, ...(this.o.meta || {}) });
      this.stop('error');
    };

    // Level meter. Failure here is not fatal: we still record, just without the meter.
    this.analyser = null;
    if (this.ctx) {
      try {
        const src = this.ctx.createMediaStreamSource(stream);
        const an = this.ctx.createAnalyser();
        an.fftSize = 1024;
        an.smoothingTimeConstant = 0.3;
        src.connect(an);
        this.analyser = an;
        this.buf = new Uint8Array(an.fftSize);
        if (this.ctx.state === 'suspended' && this.ctx.resume) await this.ctx.resume().catch(() => {});
      } catch (e) {
        log.warn('meter_unavailable', { errName: e && e.name, message: e && e.message });
      }
    }

    stream.getAudioTracks().forEach((t) => {
      t.addEventListener('ended', () => {
        if (this.state === 'recording') { log.warn('mic_track_ended', { ...(this.o.meta || {}) }); this.stop('track_ended'); }
      });
    });

    try {
      rec.start(TIMESLICE_MS);
    } catch (e) {
      this.cleanup();
      this.state = 'idle';
      throw e;
    }
    this.startedAt = performance.now();
    this.state = 'recording';
    log.info('record_start', { mimeType: this.mimeType || '(default)', meter: !!this.analyser, ...(this.o.meta || {}) });
    this.tick = setInterval(() => this.onTick(), 250);
    this.meterLoop();
    return true;
  }

  level() {
    if (!this.analyser) return null;
    this.analyser.getByteTimeDomainData(this.buf);
    let sum = 0;
    for (let i = 0; i < this.buf.length; i++) { const v = (this.buf[i] - 128) / 128; sum += v * v; }
    return Math.sqrt(sum / this.buf.length);
  }

  meterLoop() {
    if (this.state !== 'recording') return;
    if (this.o.onLevel) { try { this.o.onLevel(this.level()); } catch { /* ignore */ } }
    this.raf = requestAnimationFrame(() => this.meterLoop());
  }

  onTick() {
    if (this.state !== 'recording') return;
    const sec = this.elapsed();
    if (this.o.onTick) { try { this.o.onTick(sec); } catch { /* ignore */ } }
    if (this.o.maxSeconds && sec >= this.o.maxSeconds) {
      log.info('record_auto_stop', { durationSec: Math.round(sec), ...(this.o.meta || {}) });
      this.stop('auto');
    }
  }

  /** reason: user | auto | track_ended | error | navigate */
  stop(reason = 'user') {
    if (this.state !== 'recording' && this.state !== 'starting') return;
    if (this.state === 'starting') { this.cancelStart = true; return; }
    this.state = 'stopping';
    this.stopReason = reason;
    this.durationSec = this.elapsed();
    clearInterval(this.tick);
    cancelAnimationFrame(this.raf);
    // Safety net: some browsers never fire onstop after an error.
    this.finishTimer = setTimeout(() => this.finish(), 4000);
    try {
      if (this.rec && this.rec.state !== 'inactive') this.rec.stop();
      else this.finish();
    } catch (e) {
      log.warn('recorder_stop_failed', { errName: e && e.name });
      this.finish();
    }
  }

  finish() {
    if (this.state !== 'stopping') return;
    clearTimeout(this.finishTimer);
    const type = this.mimeType || (this.chunks[0] && this.chunks[0].type) || 'audio/webm';
    const blob = new Blob(this.chunks, { type });
    const durationSec = this.durationSec;
    const reason = this.stopReason || 'user';
    const chunkCount = this.chunks.length;
    this.cleanup();
    this.state = 'idle';
    log.info('record_stop', { durationSec: Math.round(durationSec * 10) / 10, bytes: blob.size, mimeType: type, reason, chunks: chunkCount, ...(this.o.meta || {}) });
    if (this.o.onStop) this.o.onStop({ blob, mimeType: type, durationSec, reason, chunkCount });
  }

  cleanup() {
    clearInterval(this.tick);
    cancelAnimationFrame(this.raf);
    if (this.stream) { this.stream.getTracks().forEach((t) => { try { t.stop(); } catch { /* ignore */ } }); }
    this.stream = null;
    this.analyser = null;
    if (this.ctx && this.ctx.close) { this.ctx.close().catch(() => {}); }
    this.ctx = null;
    this.rec = null;
  }
}
